import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import categoryService from "../../repository/categoryRepository";

const AddMainCategoryModal = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [formData, setFormData] = useState({
    mainCategoryName: "",
    description: "",
    categoryIcon: null,
    categoryIconName: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    setFormData((prev) => ({
      ...prev,
      categoryIcon: file,
      categoryIconName: file ? file.name : "",
    }));
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("mainCategoryName", formData.mainCategoryName);
      formDataToSend.append("description", formData.description);

      if (formData.categoryIcon instanceof File) {
        formDataToSend.append("imageURL", formData.categoryIcon);
      } else {
        console.error("categoryIcon is not a valid File object");
        return;
      }

      const response = await categoryService.addMainCategory(formDataToSend);

      if (response.success) {
        setSnackbarMessage("Main category added successfully!");
        setSnackbarSeverity("success");
        if (onSave && typeof onSave === "function") {
          onSave(response.data);
        }
        onClose();
      } else {
        setSnackbarMessage("Error adding main category.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      setSnackbarMessage("Error: " + error.message);
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor:
              colors.background || theme.palette.background.default,
            color: colors.text || theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary || theme.palette.primary.main,
            color:
              theme.palette.mode === "light"
                ? "black"
                : colors.light || theme.palette.grey[100],
            position: "relative",
          }}
        >
          Add Main Category
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.grey || theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            name="mainCategoryName"
            label="Main Category Name"
            value={formData.mainCategoryName}
            onChange={handleInputChange}
            sx={{
              mb: "10px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[700]
                      : theme.palette.grey[300],
                },
                "&:hover fieldset": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[500]
                      : theme.palette.grey[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[300]
                      : theme.palette.primary.main,
                },
              },
              "& .MuiInputLabel-root": {
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[300]
                    : theme.palette.text.primary,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[200]
                    : theme.palette.primary.main,
              },
            }}
          />
          <TextField
            fullWidth
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleInputChange}
            sx={{
              mb: "10px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[700]
                      : theme.palette.grey[300],
                },
                "&:hover fieldset": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[500]
                      : theme.palette.grey[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[300]
                      : theme.palette.primary.main,
                },
              },
              "& .MuiInputLabel-root": {
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[300]
                    : theme.palette.text.primary,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[200]
                    : theme.palette.primary.main,
              },
            }}
          />
          <Box display="flex" flexDirection="column" gap="10px" mt="10px">
            <Box display="flex" alignItems="center" gap="10px">
              <Button
                variant="contained"
                component="label"
                color="secondary"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: "10px" }}
              >
                Upload Icon
                <input
                  type="file"
                  name="categoryIcon"
                  hidden
                  onChange={handleIconUpload}
                />
              </Button>
              {formData.categoryIconName && (
                <Typography variant="body2" color={theme.palette.text.primary}>
                  Uploaded: {formData.categoryIconName}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.greenAccent || theme.palette.success.main
                  : colors.blueAccent || theme.palette.primary.main,
              color: "black",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.greenAccent || theme.palette.success.main
                    : colors.blueAccent || theme.palette.primary.main,
              },
            }}
          >
            Add Main Category
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddMainCategoryModal;
